/**
 * WordPress dependencies
 */
import { store, getContext, getElement } from '@wordpress/interactivity';

const { state, actions } = store( 'outermost/mega-menu', {
	state: {
		get isMenuOpen() {
			// The menu is opened if either `click` or `focus` is true.
			return (
				Object.values( state.menuOpenedBy ).filter( Boolean ).length > 0
			);
		},
		get menuOpenedBy() {
			const context = getContext();
			return context.menuOpenedBy;
		},
	},
	actions: {
		toggleMenuOnClick() {
			const context = getContext();
			const { ref } = getElement();
			// Safari won't send focus to the clicked element, so we need to manually place it: https://bugs.webkit.org/show_bug.cgi?id=22261
			if ( window.document.activeElement !== ref ) ref.focus();

			const isOpen = state.menuOpenedBy.click || state.menuOpenedBy.focus;
			if (!isOpen ) {
				context.previousFocus = ref;
				actions.openMenu( 'click' );
			} 
			if ( isOpen ) {
				actions.closeMenu( 'click' );
				actions.closeMenu( 'focus' );

			}
		},
		closeMenuOnClick() {
			actions.closeMenu( 'click' );
			actions.closeMenu( 'focus' );
		},
		handleMenuKeydown( event ) {
			if ( state.menuOpenedBy.click ) {
				// If Escape close the menu.
				if ( event?.key === 'Escape' ) {
					actions.closeMenu( 'click' );
					actions.closeMenu( 'focus' );
				}
			}
		},
		handleMenuFocusout( event ) {
			const context = getContext();
			const menuContainer = context.megaMenu?.querySelector(
				'.wp-block-outermost-mega-menu__menu-container'
			);
			// If focus is outside menu, and in the document, close menu
			// event.target === The element losing focus
			// event.relatedTarget === The element receiving focus (if any)
			// When focusout is outside the document,
			// `window.document.activeElement` doesn't change.

			// The event.relatedTarget is null when something outside the navigation menu is clicked. This is only necessary for Safari.
			// TODO: There is still an issue in Safari where clicking on the menu link closes the menu. We don't want this. The toggleMenuOnClick callback should handle this.
			if (
				event.relatedTarget === null ||
				( ! menuContainer?.contains( event.relatedTarget ) &&
					event.target !== window.document.activeElement )
			) {
				actions.closeMenu( 'click' );
				actions.closeMenu( 'focus' );
			}
		},
		openMenu( menuOpenedOn = 'click' ) {
			state.menuOpenedBy[ menuOpenedOn ] = true;
		},
		closeMenu( menuClosedOn = 'click' ) {
			const context = getContext();
			state.menuOpenedBy[ menuClosedOn ] = false;

			// Reset the menu reference and button focus when closed.
			if ( ! state.isMenuOpen ) {
				if (
					context.megaMenu?.contains( window.document.activeElement )
				) {
					context.previousFocus?.focus();
				}
				context.previousFocus = null;
				context.megaMenu = null;
			}
		},
	},
	callbacks: {
		initMenu() {
			const context = getContext();
			const { ref } = getElement();
			const openedByClick = Object.values( context.menuOpenedBy ).filter( Boolean ).length > 0
			// Set the menu reference when initialized.
			if ( state.isMenuOpen ) {
				context.megaMenu = ref;
				//make background darker
                document.body.classList.add('menu-expanded');
			}else {
                document.body.classList.remove('menu-expanded');

			}
			// // Add hover event listeners
			ref.addEventListener('mouseenter', function(element) {
				// Get the toggle button inside the element
				const toggleButton = ref.querySelector('.wp-block-outermost-mega-menu__toggle');
				if (toggleButton && !openedByClick) {
					// Set aria-expanded to true for the current menu
					toggleButton.setAttribute('aria-expanded', 'true');
					document.body.classList.add('menu-expanded');

				}
			});
			ref.addEventListener('mouseleave',  function(element) {
				const openedByClick = Object.values( context.menuOpenedBy ).filter( Boolean ).length > 0
				// Get the toggle button inside the element
				const toggleButton = ref.querySelector('.wp-block-outermost-mega-menu__toggle');
				if (toggleButton && !openedByClick) {
					// Set aria-expanded to true for the current menu
					setTimeout(function(){toggleButton.setAttribute('aria-expanded', 'false')}, 100);
					document.body.classList.remove('menu-expanded');
				}
			
			})
		},
	},
} );

// Function to convert a complex CSS value to pixels
function convertCssValueToPixels( cssValue ) {
	// Create a temporary element
	const tempElement = document.createElement( 'div' );

	// Apply the CSS value to the temporary element
	// For example, setting its width to the complex CSS value
	tempElement.style.width = cssValue;

	// Append the temporary element to the body to make it part of the document
	document.body.appendChild( tempElement );

	// Use getComputedStyle to get the computed width in pixels
	const computedWidth = window.getComputedStyle( tempElement ).width;

	// Remove the temporary element from the document
	document.body.removeChild( tempElement );

	// Return the computed width as a number
	return parseFloat( computedWidth );
}
